import type {
  AgentConnectionState,
  AgentState,
  ReploAgent,
} from "@agent-ui/features/chat/hooks/useReploAgentConnection";
import type { OpencodeEvent } from "schemas/opencode";

import * as React from "react";

import { useReploAgentConnection } from "@agent-ui/features/chat/hooks/useReploAgentConnection";
import { SandboxNavigationProvider } from "@agent-ui/store/sandboxNavigationStore";
import { toast } from "@replo/design-system/components/toast/Toast";
import { useDebugPublishStatusOverride } from "@v2/features/debug/debugPublishStatusOverrideStore";
import { parseJsonOrNull } from "replo-utils/lib/json";
import { useRequiredContext } from "replo-utils/react/context";
import { agentToClientMessageSchema } from "schemas/agentConnection";

/**
 * NOTE (Noah, 2026-02-03): ReploAgentContext
 *
 * Provides the ReploAgent connection at the project level so it can be shared
 * across multiple chats. This is conditionally enabled based on the project's
 * agentEnabledAt field.
 */

interface AgentConnectionContextValue {
  agent: ReploAgent;
  state: AgentState;
  /** Un-overridden state from the DO, for use by the debug panel only. */
  _rawState: AgentState;
  connectionState: AgentConnectionState;
  actions: ReturnType<typeof useReploAgentConnection>["actions"];
}

const AgentConnectionContext = React.createContext<
  AgentConnectionContextValue | undefined
>(undefined);

/**
 * Hook to access the project-level agent connection.
 * Can optionally subscribe to OpenCode events.
 */
export const useOptionalReploAgent = () => {
  return React.useContext(AgentConnectionContext) ?? null;
};

export const useReploAgent = (opts?: {
  onEvent?: (event: OpencodeEvent) => void;
}) => {
  const contextValue = useRequiredContext(AgentConnectionContext);
  const onEvent = opts?.onEvent ?? null;
  const agent = contextValue?.agent;

  React.useEffect(() => {
    if (!onEvent || !agent) {
      return;
    }
    const handleEvent = (event: { data: string }) => {
      const json = parseJsonOrNull(event.data);
      const parsed = agentToClientMessageSchema.safeParse(json);
      if (!parsed.success) {
        return;
      }
      if (parsed.data.type === "opencode-event") {
        onEvent(parsed.data.payload);
      }
    };
    agent.addEventListener("message", handleEvent);
    return () => {
      agent.removeEventListener("message", handleEvent);
    };
  }, [onEvent, agent]);

  return contextValue;
};

/**
 * Provider that wraps children with the agent connection.
 * This should be used within CurrentProjectProvider when agent mode is
 * enabled for the project.
 */
export const ReploAgentProvider: React.FC<
  React.PropsWithChildren<{
    projectId: string;
    agentToken: string;
  }>
> = ({ projectId, agentToken, children }) => {
  return (
    <SandboxNavigationProvider>
      <ReploAgentConnectionProvider
        projectId={projectId}
        agentToken={agentToken}
      >
        {children}
      </ReploAgentConnectionProvider>
    </SandboxNavigationProvider>
  );
};

const ReploAgentConnectionProvider: React.FC<
  React.PropsWithChildren<{
    projectId: string;
    agentToken: string;
  }>
> = ({ projectId, agentToken, children }) => {
  const { agent, state, connectionState, actions } = useReploAgentConnection({
    projectId,
    agentToken,
    onError: (error) => {
      console.error("[ReploAgentProvider] Agent connection error:", error);
      toast.error({
        title: "Connection Error",
        description: error.message || "Failed to connect to agent",
      });
    },
  });

  const publishStatusOverride = useDebugPublishStatusOverride();
  const mergedState = React.useMemo(() => {
    if (!publishStatusOverride) {
      return state;
    }
    return { ...state, publishStatus: publishStatusOverride };
  }, [state, publishStatusOverride]);

  const contextValue = React.useMemo(
    () => ({
      agent,
      state: mergedState,
      _rawState: state,
      connectionState,
      actions,
    }),
    [agent, mergedState, state, connectionState, actions],
  );

  return (
    <AgentConnectionContext.Provider value={contextValue}>
      {children}
    </AgentConnectionContext.Provider>
  );
};
